import { useEffect, useRef } from "react";
import { motion, useReducedMotion } from "motion/react";
import { animate } from "animejs";
import { profile } from "../data";

const SILK: [number, number, number, number] = [0.22, 1, 0.36, 1];

const cardFacts = [
  { label: "Studying", value: "B.Tech, Computer Engineering" },
  { label: "Experience", value: "6 months on a product team" },
  { label: "Based in", value: "Thalassery, Kerala" },
];

function AvailabilityPill() {
  return (
    <span className="glass pill inline-flex items-center gap-2.5 !rounded-full px-4 py-2 text-[0.78rem] text-mist">
      <span className="relative grid h-2 w-2 place-items-center text-blush">
        <span className="h-2 w-2 rounded-full bg-blush" />
        <span className="pulse-ring absolute inset-0" />
      </span>
      {profile.availability}
    </span>
  );
}

function Monogram() {
  const ringRef = useRef<HTMLSpanElement>(null);
  const reduce = useReducedMotion();

  useEffect(() => {
    if (reduce || !ringRef.current) return;
    animate(ringRef.current, {
      rotate: 360,
      duration: 24000,
      ease: "linear",
      loop: true,
    });
  }, [reduce]);

  return (
    <div className="relative mx-auto grid h-40 w-40 place-items-center">
      <span
        ref={ringRef}
        aria-hidden="true"
        className="absolute inset-0 rounded-full opacity-90 blur-[1px]"
        style={{
          background:
            "conic-gradient(from 0deg, transparent 4%, #eba6c6 24%, #bea7f2 46%, #8fbcff 66%, transparent 88%)",
          maskImage: "radial-gradient(farthest-side, transparent calc(100% - 2px), #000 0)",
          WebkitMaskImage: "radial-gradient(farthest-side, transparent calc(100% - 2px), #000 0)",
        }}
      />
      <span className="absolute inset-[10px] rounded-full bg-midnight/45 backdrop-blur-md" />
      <span className="text-display mist-gradient relative text-[3.6rem] leading-none font-medium">
        {profile.monogram}
      </span>
    </div>
  );
}

function CallingCard() {
  return (
    <div className="relative">
      <div
        aria-hidden="true"
        className="drift absolute -inset-10 -z-10 rounded-full opacity-70 blur-3xl"
        style={{
          background:
            "radial-gradient(45% 45% at 30% 30%, rgba(235,166,198,0.5), transparent 70%), radial-gradient(50% 50% at 75% 70%, rgba(143,188,255,0.42), transparent 72%)",
        }}
      />
      <div className="glass halo leaf relative px-7 py-9 sm:px-9">
        <Monogram />

        <div className="mt-7 text-center">
          <p className="text-display text-[1.3rem] text-porcelain">
            {profile.first} <span className="text-display-italic text-blush/95">{profile.last}</span>
          </p>
          <p className="mt-1.5 text-[0.86rem] text-mist">{profile.role}</p>
        </div>

        <dl className="mt-8 space-y-3 border-t border-white/[0.09] pt-6">
          {cardFacts.map((fact) => (
            <div key={fact.label} className="flex items-baseline justify-between gap-4">
              <dt className="text-[0.78rem] whitespace-nowrap text-faint">{fact.label}</dt>
              <dd className="text-right text-[0.86rem] text-porcelain/90">{fact.value}</dd>
            </div>
          ))}
        </dl>

        <p className="mt-6 text-center text-display-italic text-[0.86rem] text-orchid/80">
          {profile.timezone}
        </p>
      </div>
    </div>
  );
}

export function Hero() {
  const reduce = useReducedMotion();

  const enter = (delay: number) =>
    reduce
      ? { initial: false as const }
      : {
          initial: { opacity: 0, y: 22, filter: "blur(14px)" },
          animate: { opacity: 1, y: 0, filter: "blur(0px)" },
          transition: { duration: 0.95, delay, ease: SILK },
        };

  const line = (delay: number) =>
    reduce
      ? { initial: false as const }
      : {
          initial: { y: "118%", rotate: 2.5 },
          animate: { y: "0%", rotate: 0 },
          transition: { duration: 1.05, delay, ease: SILK },
        };

  return (
    <section id="top" className="relative pt-32 pb-20 sm:pt-40 sm:pb-28">
      <div className="grid items-center gap-14 lg:grid-cols-[1.35fr_0.9fr] lg:gap-20">
        <div>
          <motion.div {...enter(0.05)}>
            <AvailabilityPill />
          </motion.div>

          <h1 className="text-display mt-7 text-[clamp(3rem,9vw,6rem)] leading-[0.95] font-medium">
            <span className="mask-line">
              <motion.span className="block text-porcelain" {...line(0.16)}>
                {profile.first}
              </motion.span>
            </span>
            <span className="mask-line">
              <motion.span className="block mist-gradient" {...line(0.26)}>
                {profile.last}
              </motion.span>
            </span>
          </h1>

          <motion.p className="mt-6 text-[1.05rem] text-porcelain/85 sm:text-[1.12rem]" {...enter(0.42)}>
            {profile.role}
          </motion.p>

          <motion.p className="mt-5 max-w-[46ch] leading-relaxed text-mist" {...enter(0.52)}>
            {profile.tagline}
          </motion.p>

          <motion.div className="mt-9 flex flex-wrap items-center gap-3" {...enter(0.64)}>
            <a href="#work" className="btn btn-primary">
              See my work
            </a>
            <a href="#contact" className="btn btn-ghost">
              Get in touch
            </a>
            <a
              href={profile.linkedin}
              target="_blank"
              rel="noreferrer"
              className="link-underline ml-1 text-[0.9rem] text-mist transition-colors hover:text-porcelain"
            >
              LinkedIn
            </a>
          </motion.div>

          <motion.p className="mt-8 text-[0.82rem] text-faint" {...enter(0.74)}>
            {profile.pronouns} · {profile.location}
          </motion.p>
        </div>

        <motion.div
          {...(reduce
            ? { initial: false as const }
            : {
                initial: { opacity: 0, scale: 0.94, rotate: -1.5, filter: "blur(18px)" },
                animate: { opacity: 1, scale: 1, rotate: 0, filter: "blur(0px)" },
                transition: { duration: 1.15, delay: 0.4, ease: SILK },
              })}
        >
          <CallingCard />
        </motion.div>
      </div>
    </section>
  );
}
